<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useContactNotes } from 'dashboard/composables/useContactNotes';
import { useAlert } from 'dashboard/composables';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactStageChip from './ContactStageChip.vue';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';
import { channelLabel } from 'dashboard/components-next/Contacts/contactChannel';
import {
  shortDate,
  relativeTime as formatRelativeTime,
} from 'dashboard/components-next/Contacts/contactTime';

// Left column of the contact detail (CrmB): avatar + name + company, stage
// chips, key facts, labels and a 2x2 grid of counters. Everything here is
// derived from stores that ContactManageView already loads.
const props = defineProps({
  contact: { type: Object, required: true },
});

const { t, locale } = useI18n();
const store = useStore();

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const conversationsByContact = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const tasksByContact = useMapGetter('contactTasks/getAllTasksByContactId');
const inboxById = useMapGetter('inboxes/getInboxById');

const contactId = computed(() => props.contact.id);

// Mismo total que la pestaña "Notas": de contacto y privadas juntas.
const { allNotes } = useContactNotes(contactId);

// Un chip por cada atributo de contacto sincronizado con etiquetas —Tipo,
// Estado y los que añada la cuenta—, siempre visible aunque esté vacío para
// poder asignarlo desde aquí.
const chipAttributes = computed(() =>
  syncedListAttributes(contactAttributes.value)
);
const attributeValue = attribute =>
  attribute ? props.contact.customAttributes?.[attribute.attributeKey] : '';

const updateAttribute = async (attribute, value) => {
  try {
    await store.dispatch('contacts/update', {
      id: contactId.value,
      customAttributes: { [attribute.attributeKey]: value },
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  }
};

const conversations = computed(
  () => conversationsByContact.value(contactId.value) || []
);
const latestConversation = computed(
  () =>
    [...conversations.value].sort(
      (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
    )[0]
);
const latestInbox = computed(() =>
  latestConversation.value
    ? inboxById.value(latestConversation.value.inboxId)
    : null
);
const latestChannel = computed(() =>
  channelLabel(
    latestConversation.value?.meta?.channel || latestInbox.value?.channelType
  )
);

const formatDate = timestamp => shortDate(timestamp, locale.value);
const relativeTime = timestamp => formatRelativeTime(timestamp, locale.value);

const metaRows = computed(() => {
  const lastActivity = props.contact.lastActivityAt
    ? [relativeTime(props.contact.lastActivityAt), latestChannel.value]
        .filter(Boolean)
        .join(' · ')
    : t('CONTACTS_LAYOUT.DETAILS.SUMMARY.NEVER');
  const agent =
    latestConversation.value?.meta?.assignee?.name ||
    t('CONTACTS_LAYOUT.DETAILS.SUMMARY.UNASSIGNED');
  const inbox = latestInbox.value
    ? [latestInbox.value.name, latestChannel.value].filter(Boolean).join(' · ')
    : '—';
  const { whatsappUsername, whatsappUserId } =
    props.contact.additionalAttributes || {};
  return [
    {
      key: 'created',
      label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.CREATED'),
      value: formatDate(props.contact.createdAt) || '—',
    },
    ...(whatsappUsername
      ? [
          {
            key: 'whatsapp_username',
            label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.WHATSAPP_USERNAME'),
            value: `@${whatsappUsername}`,
          },
        ]
      : []),
    ...(whatsappUserId
      ? [
          {
            key: 'whatsapp_user_id',
            label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.WHATSAPP_USER_ID'),
            value: whatsappUserId,
          },
        ]
      : []),
    {
      key: 'activity',
      label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.LAST_ACTIVITY'),
      value: lastActivity,
    },
    {
      key: 'agent',
      label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.AGENT'),
      value: agent,
    },
    {
      key: 'inbox',
      label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.INBOX'),
      value: inbox,
    },
  ];
});

const stats = computed(() => {
  const open = conversations.value.filter(c => c.status === 'open').length;
  return [
    {
      key: 'conversations',
      label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.STATS.CONVERSATIONS'),
      value: conversations.value.length,
    },
    {
      key: 'open',
      label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.STATS.OPEN'),
      value: open,
    },
    {
      key: 'tasks',
      label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.STATS.TASKS'),
      value: (tasksByContact.value(contactId.value) || []).length,
    },
    {
      key: 'notes',
      label: t('CONTACTS_LAYOUT.DETAILS.SUMMARY.STATS.NOTES'),
      value: allNotes.value.length,
    },
  ];
});

const companyName = computed(
  () =>
    props.contact.company?.name ||
    props.contact.additionalAttributes?.companyName ||
    ''
);

const handleAvatarUpload = async ({ file }) => {
  try {
    await store.dispatch('contacts/update', {
      id: contactId.value,
      name: props.contact.name,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    await store.dispatch('contacts/deleteAvatar', contactId.value);
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message || t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <aside
    class="flex flex-col gap-3.5 p-[18px] rounded-xl border border-n-weak bg-n-solid-2"
  >
    <div class="flex items-center gap-3 min-w-0">
      <Avatar
        :src="contact.thumbnail || ''"
        :name="contact.name || ''"
        :size="48"
        allow-upload
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-0.5 min-w-0">
        <span
          class="text-[17px] font-semibold tracking-tight truncate text-n-slate-12"
        >
          {{ contact.name }}
        </span>
        <span v-if="companyName" class="text-xs truncate text-n-slate-10">
          {{ companyName }}
        </span>
      </div>
    </div>

    <div v-if="chipAttributes.length" class="flex flex-wrap gap-1.5">
      <ContactStageChip
        v-for="(attribute, index) in chipAttributes"
        :key="attribute.id"
        :attribute="attribute"
        :value="attributeValue(attribute)"
        :show-dot="index === 0"
        @update="updateAttribute(attribute, $event)"
      />
    </div>

    <div class="h-px bg-n-weak" />

    <dl class="flex flex-col gap-2 m-0">
      <div
        v-for="row in metaRows"
        :key="row.key"
        class="flex items-baseline gap-2.5"
      >
        <dt class="flex-shrink-0 w-24 text-xs text-n-slate-10">
          {{ row.label }}
        </dt>
        <dd
          class="m-0 text-[12.5px] font-medium truncate text-n-slate-12"
          :title="row.value"
        >
          {{ row.value }}
        </dd>
      </div>
    </dl>

    <div class="h-px bg-n-weak" />

    <div class="flex flex-col gap-2">
      <span
        class="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-n-slate-10"
      >
        {{ t('CONTACTS_LAYOUT.DETAILS.SUMMARY.LABELS') }}
      </span>
      <ContactLabels :contact-id="contactId" />
    </div>

    <div class="h-px bg-n-weak" />

    <div class="grid grid-cols-2 gap-2.5">
      <div v-for="stat in stats" :key="stat.key" class="flex flex-col gap-0.5">
        <span
          class="text-[17px] font-semibold tracking-tight tabular-nums text-n-slate-12"
        >
          {{ stat.value }}
        </span>
        <span class="text-[11px] text-n-slate-10">{{ stat.label }}</span>
      </div>
    </div>
  </aside>
</template>
